import React from "react";
import Topbar from "../menu/Topbar";
import RealgemsSidebar from "./RealgemsSidebar";
import Api from "../Services/Api";
import { useAuth } from "../auth/AuthContext";
import { isRealgemsAdmin, normalizeAccessType } from "../../utils/accessControl";
import "./RealgemsDashboard.css";

const MONTH_FORMATTER = new Intl.DateTimeFormat("pt-BR", {
  month: "short",
});

const getMonthKey = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;

  const month = String(date.getMonth() + 1).padStart(2, "0");
  return `${date.getFullYear()}-${month}`;
};

const getMonthLabel = (key) => {
  const [year, month] = String(key || "").split("-");
  const date = new Date(Number(year), Number(month) - 1, 1);
  if (Number.isNaN(date.getTime())) return key;

  const label = MONTH_FORMATTER.format(date).replace(".", "");
  return `${label}/${String(year).slice(-2)}`;
};

const buildMonthlySeries = (clients = [], monthsBack = 6) => {
  const buckets = [];
  const now = new Date();

  for (let index = monthsBack - 1; index >= 0; index -= 1) {
    const date = new Date(now.getFullYear(), now.getMonth() - index, 1);
    const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
    buckets.push({ key, label: getMonthLabel(key), value: 0 });
  }

  const bucketMap = new Map(buckets.map((bucket) => [bucket.key, bucket]));
  clients.forEach((client) => {
    const key = getMonthKey(client.createdAt || client.updatedAt);
    if (!key || !bucketMap.has(key)) return;
    bucketMap.get(key).value += 1;
  });

  return buckets;
};

const buildTopSegments = (clients = [], limit = 5) => {
  const map = new Map();

  clients.forEach((client) => {
    const name = String(client.businessSegment || "Não informado").trim() || "Não informado";
    map.set(name, (map.get(name) || 0) + 1);
  });

  return [...map.entries()]
    .map(([label, value]) => ({ label, value }))
    .sort((a, b) => b.value - a.value || a.label.localeCompare(b.label))
    .slice(0, limit);
};

const buildRecentClients = (clients = [], limit = 5) =>
  [...clients]
    .sort((a, b) => {
      const aTime = new Date(a.createdAt || a.updatedAt || 0).getTime();
      const bTime = new Date(b.createdAt || b.updatedAt || 0).getTime();
      return bTime - aTime;
    })
    .slice(0, limit);

const formatShortDate = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Sem data";
  return new Intl.DateTimeFormat("pt-BR").format(date);
};

const maxValue = (items = []) => Math.max(1, ...items.map((item) => Number(item.value || item.totalClients || 0)));

const RealgemsDashboard = () => {
  const { user } = useAuth();
  const [menuClose, setMenuClose] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [summary, setSummary] = React.useState(null);
  const [clients, setClients] = React.useState([]);

  const adminViewer = isRealgemsAdmin(normalizeAccessType(user?.access_type));

  React.useEffect(() => {
    let mounted = true;

    const loadDashboard = async () => {
      try {
        setLoading(true);
        setError("");

        const [summaryResponse, clientsResponse] = await Promise.all([
          Api.get("/realgems/clients/summary"),
          Api.get("/realgems/clients"),
        ]);

        if (!mounted) return;

        setSummary(summaryResponse?.data?.summary || null);
        setClients(Array.isArray(clientsResponse?.data?.items) ? clientsResponse.data.items : []);
      } catch (err) {
        console.error("Erro ao carregar dashboard Realgems:", err);
        if (!mounted) return;
        setError(err?.response?.data?.message || "Não foi possível carregar os indicadores do dashboard.");
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    loadDashboard();

    return () => {
      mounted = false;
    };
  }, []);

  const totalClients = Number(summary?.totalClients || 0);
  const totalOwners = Number(summary?.totalOwners || 0);
  const ownerRows = Array.isArray(summary?.owners) ? summary.owners : [];
  const monthlySeries = buildMonthlySeries(clients, 6);
  const topSegments = buildTopSegments(clients, 5);
  const recentClients = buildRecentClients(clients, 5);
  const monthlyMax = maxValue(monthlySeries);
  const segmentMax = maxValue(topSegments);
  const ownerMax = maxValue(ownerRows);

  return (
    <div className="app-container realgems-dashboard-page">
      <RealgemsSidebar close={menuClose} />

      <main className="main-content realgems-main-content">
        <Topbar
          page="Dashboard Realgems"
          onToggleMenu={() => setMenuClose((current) => !current)}
        />

        {error ? <div className="realgems-dashboard-alert">{error}</div> : null}

        <section className="realgems-dashboard-grid">
          <article className="realgems-dashboard-stat featured">
            <span className="realgems-dashboard-stat-label">
              {adminViewer ? "Cadastros gerais" : "Meus clientes cadastrados"}
            </span>
            <strong>{loading ? "..." : totalClients}</strong>
            <p>
              {adminViewer
                ? "Total de clientes cadastrados por todos os usuários Realgems."
                : "Total de clientes cadastrados no seu usuário Realgems."}
            </p>
          </article>

          <article className="realgems-dashboard-stat alt">
            <span className="realgems-dashboard-stat-label">Cadastros no mês</span>
            <strong>{loading ? "..." : monthlySeries[monthlySeries.length - 1]?.value || 0}</strong>
            <p>Quantidade de clientes cadastrados no mês atual.</p>
          </article>

          {adminViewer ? (
            <article className="realgems-dashboard-stat soft">
              <span className="realgems-dashboard-stat-label">Usuários com cadastro</span>
              <strong>{loading ? "..." : totalOwners}</strong>
              <p>Usuários Realgems que já cadastraram pelo menos um cliente.</p>
            </article>
          ) : (
            <article className="realgems-dashboard-stat soft">
              <span className="realgems-dashboard-stat-label">Último cadastro</span>
              <strong>{loading ? "..." : recentClients[0] ? formatShortDate(recentClients[0].createdAt) : "--"}</strong>
              <p>Data do cliente mais recente cadastrado no seu acesso.</p>
            </article>
          )}
        </section>

        <section className="realgems-dashboard-analytics">
          <article className="realgems-dashboard-panel">
            <div className="realgems-dashboard-section-head">
              <div>
                <span className="realgems-eyebrow light">Evolução</span>
                <h2>Cadastros por mês</h2>
                <p>Visualização dos últimos 6 meses.</p>
              </div>
            </div>

            {loading ? (
              <div className="realgems-dashboard-empty">Carregando gráfico...</div>
            ) : (
              <div className="realgems-dashboard-bars monthly">
                {monthlySeries.map((item) => (
                  <div className="realgems-dashboard-bar-item" key={item.key}>
                    <span className="realgems-dashboard-bar-value">{item.value}</span>
                    <div className="realgems-dashboard-bar-track">
                      <div
                        className="realgems-dashboard-bar-fill"
                        style={{ height: `${Math.max(8, (item.value / monthlyMax) * 100)}%` }}
                      />
                    </div>
                    <span className="realgems-dashboard-bar-label">{item.label}</span>
                  </div>
                ))}
              </div>
            )}
          </article>

          <article className="realgems-dashboard-panel">
            <div className="realgems-dashboard-section-head">
              <div>
                <span className="realgems-eyebrow light">Segmentos</span>
                <h2>Top segmentos</h2>
                <p>Concentração dos clientes por segmento de atuação.</p>
              </div>
            </div>

            {loading ? (
              <div className="realgems-dashboard-empty">Carregando gráfico...</div>
            ) : topSegments.length === 0 ? (
              <div className="realgems-dashboard-empty">Ainda não há segmentos para exibir.</div>
            ) : (
              <div className="realgems-dashboard-ranked-list">
                {topSegments.map((item) => (
                  <div className="realgems-dashboard-ranked-item" key={item.label}>
                    <div className="realgems-dashboard-ranked-meta">
                      <strong>{item.label}</strong>
                      <span>{item.value} cliente(s)</span>
                    </div>
                    <div className="realgems-dashboard-ranked-track">
                      <div
                        className="realgems-dashboard-ranked-fill"
                        style={{ width: `${(item.value / segmentMax) * 100}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </article>
        </section>

        {adminViewer ? (
          <section className="realgems-dashboard-owners">
            <div className="realgems-dashboard-section-head">
              <div>
                <span className="realgems-eyebrow light">Por usuário</span>
                <h2>Quantidade por usuário cadastrado</h2>
                <p>Acompanhe quantos clientes cada usuário Realgems cadastrou no portal.</p>
              </div>
            </div>

            {loading ? (
              <div className="realgems-dashboard-empty">Carregando usuários...</div>
            ) : ownerRows.length === 0 ? (
              <div className="realgems-dashboard-empty">
                Nenhum cliente cadastrado ainda no portal Realgems.
              </div>
            ) : (
              <div className="realgems-dashboard-owner-list chart">
                {ownerRows.map((owner) => (
                  <article className="realgems-dashboard-owner-card chart" key={owner.ownerUserId}>
                    <div className="realgems-dashboard-owner-main">
                      <strong>{owner.ownerUserName || "Usuário sem nome"}</strong>
                      <span>{owner.ownerUserEmail || "E-mail não informado"}</span>
                    </div>
                    <div className="realgems-dashboard-owner-bar">
                      <div
                        className="realgems-dashboard-owner-bar-fill"
                        style={{ width: `${(owner.totalClients / ownerMax) * 100}%` }}
                      />
                    </div>
                    <div className="realgems-dashboard-owner-total">
                      {owner.totalClients} cliente(s)
                    </div>
                  </article>
                ))}
              </div>
            )}
          </section>
        ) : (
          <section className="realgems-dashboard-panel single">
            <div className="realgems-dashboard-section-head">
              <div>
                <span className="realgems-eyebrow light">Recentes</span>
                <h2>Últimos clientes cadastrados</h2>
                <p>Seus registros mais recentes no portal Realgems.</p>
              </div>
            </div>

            {loading ? (
              <div className="realgems-dashboard-empty">Carregando clientes...</div>
            ) : recentClients.length === 0 ? (
              <div className="realgems-dashboard-empty">Nenhum cliente cadastrado ainda.</div>
            ) : (
              <div className="realgems-dashboard-recent-list">
                {recentClients.map((client) => (
                  <article className="realgems-dashboard-recent-card" key={client.id}>
                    <div>
                      <strong>{client.tradeName}</strong>
                      <span>{client.companyLegalName}</span>
                    </div>
                    <div className="realgems-dashboard-recent-date">
                      {formatShortDate(client.createdAt)}
                    </div>
                  </article>
                ))}
              </div>
            )}
          </section>
        )}
      </main>
    </div>
  );
};

export default RealgemsDashboard;
